import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { createFileRoute } from '@tanstack/react-router'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faChevronUp, faXmark, faArrowRight, faList } from '@fortawesome/free-solid-svg-icons'
import { faWhatsapp } from '@fortawesome/free-brands-svg-icons'
import { whatsAppCtaProps, trackSelectItem, trackViewItemList } from '../lib/analytics'
import {
  getProductPrice,
  listStorefrontCategories,
  listStorefrontProducts,
  placeholderImage,
  toExternalImage,
  toExternalImageSrcSet,
} from '../lib/dropify'
import type { StorefrontCategory, StorefrontProduct } from '../lib/dropify'
import { listStorefrontProductsServer } from '../lib/storefront'
import { DEFAULT_OG_IMAGE, SITE_URL, absoluteUrl, stripHtml, truncate } from '../lib/seo'
import { WhatsAppChannelSection } from '../components/WhatsAppChannelSection'

const WA_NUMBER = '6285704011028'
const CATALOG_PER_PAGE = 100
const CATALOG_IMAGE_TRANSFORM = { width: 1080, height: 1920, fitIn: true, format: 'webp', quality: 80 } as const
const CATALOG_BLUR_TRANSFORM = { width: 80, height: 142, smart: true, format: 'webp', quality: 40 } as const
const DIVIDER_IMAGE_TRANSFORM = { width: 1080, height: 1920, smart: true, format: 'webp', quality: 70 } as const
const TOC_THUMB_TRANSFORM = { width: 120, height: 120, smart: true, format: 'webp', quality: 70 } as const

const TOC_SLIDE_ID = 'daftar-isi'
const INITIAL_RENDER = 5
const EXPAND_BATCH = 15
const IMAGE_RENDER_RADIUS = 2

type CategoryFetchState = 'idle' | 'loading' | 'ready' | 'empty' | 'error'

type TocCategoryEntry = {
  id: string
  name: string
  slug: string
  productCount: number | null
  thumbUrl: string
}

type TocSlide = {
  kind: 'toc'
  entries: TocCategoryEntry[]
}

type CategoryDividerSlide = {
  kind: 'divider'
  slug: string
  name: string
  description: string
  imageUrl: string
  index: number
  total: number
}

type CategoryLoadingSlide = {
  kind: 'status'
  slug: string
  name: string
  state: 'loading' | 'empty' | 'error'
}

type ChannelSubscribeSlide = {
  kind: 'channel'
}

type SlimProductSlide = {
  kind: 'product'
  id: string
  slug: string
  name: string
  description: string
  imageUrl: string
  imageSrcSet: string | undefined
  blurImageUrl: string
  categoryName: string
  categorySlug: string
}

type CatalogSlide = TocSlide | CategoryDividerSlide | CategoryLoadingSlide | SlimProductSlide | ChannelSubscribeSlide

function dividerId(slug: string) {
  return `kategori-${slug}`
}

function productSlideId(productId: string) {
  return `produk-slide-${productId}`
}

function waProductLink(productName: string, productSlug: string) {
  const productUrl = `${SITE_URL}/produk/${productSlug}`
  const message = `Halo Dropify, saya ingin memeriksa ${productName}. Jumlah: ___. Kota tujuan: ___. Mohon konfirmasi MOQ, stok, dan quotation. Produk: ${productUrl}`
  return `https://wa.me/${WA_NUMBER}?text=${encodeURIComponent(message)}`
}

function getProductImageUrl(product: StorefrontProduct) {
  const featured = product.photos.find((p) => p.is_featured)?.url
  const first = product.photos[0]?.url
  const variant = product.variants.flatMap((v) => v.photos)[0]?.url
  return featured ?? first ?? variant ?? null
}

function buildSlimProductSlide(
  product: StorefrontProduct,
  category: { name: string; slug: string },
): SlimProductSlide {
  const sourceUrl = getProductImageUrl(product)
  const imageUrl = sourceUrl
    ? toExternalImage(sourceUrl, CATALOG_IMAGE_TRANSFORM)
    : placeholderImage(product.name, 1080, 1920)
  const blurImageUrl = sourceUrl
    ? toExternalImage(sourceUrl, CATALOG_BLUR_TRANSFORM)
    : imageUrl
  return {
    kind: 'product',
    id: product.id,
    slug: product.slug || product.id,
    name: product.name,
    description: truncate(stripHtml(product.description ?? ''), 140),
    imageUrl,
    imageSrcSet: sourceUrl ? toExternalImageSrcSet(sourceUrl, [480, 720, 1080].map((width) => ({
      width, options: { ...CATALOG_IMAGE_TRANSFORM, width, height: Math.round(width * 16 / 9) },
    }))) : '',
    blurImageUrl,
    categoryName: category.name,
    categorySlug: category.slug,
  }
}

function buildDividerSlide(
  category: StorefrontCategory,
  index: number,
  total: number,
): CategoryDividerSlide {
  const imageUrl = category.image_url
    ? toExternalImage(category.image_url, DIVIDER_IMAGE_TRANSFORM)
    : placeholderImage(category.name, 1080, 1920)
  return {
    kind: 'divider',
    slug: category.slug,
    name: category.name,
    description: truncate(stripHtml(category.description ?? ''), 180),
    imageUrl,
    index,
    total,
  }
}

function buildTocEntry(
  category: StorefrontCategory,
  productCount: number | null,
): TocCategoryEntry {
  const thumbUrl = category.image_url
    ? toExternalImage(category.image_url, TOC_THUMB_TRANSFORM)
    : placeholderImage(category.name, 120, 120)
  return {
    id: category.id,
    name: category.name,
    slug: category.slug,
    productCount,
    thumbUrl,
  }
}

function groupProductsByCategory(products: StorefrontProduct[]) {
  const map = new Map<string, StorefrontProduct[]>()
  for (const product of products) {
    const cat = product.categories[0]
    if (!cat) continue
    const list = map.get(cat.slug) ?? []
    list.push(product)
    map.set(cat.slug, list)
  }
  return map
}

function buildSlides(
  orderedCategories: StorefrontCategory[],
  productsByCategory: Map<string, StorefrontProduct[]>,
  categoryFetchStates: Record<string, CategoryFetchState>,
): CatalogSlide[] {
  const tocEntries: TocCategoryEntry[] = orderedCategories.map((category) =>
    buildTocEntry(
      category,
      categoryFetchStates[category.slug] === 'ready' || categoryFetchStates[category.slug] === 'empty'
        ? productsByCategory.get(category.slug)?.length ?? 0 : null,
    ),
  )
  const slides: CatalogSlide[] = []
  if (tocEntries.length > 0) {
    slides.push({ kind: 'toc', entries: tocEntries })
    slides.push({ kind: 'channel' })
  }
  orderedCategories.forEach((category, idx) => {
    const items = productsByCategory.get(category.slug) ?? []
    slides.push(buildDividerSlide(category, idx, orderedCategories.length))
    if (items.length === 0) {
      const state = categoryFetchStates[category.slug] === 'error'
        ? 'error'
        : categoryFetchStates[category.slug] === 'empty' ? 'empty' : 'loading'
      slides.push({
        kind: 'status',
        slug: category.slug,
        name: category.name,
        state,
      })
      return
    }
    for (const product of items) {
      slides.push(
        buildSlimProductSlide(product, { name: category.name, slug: category.slug }),
      )
    }
  })
  return slides
}

export const Route = createFileRoute('/katalog')({
  loader: async () => {
    const [categories, firstPage] = await Promise.all([
      listStorefrontCategories().catch(() => [] as StorefrontCategory[]),
      listStorefrontProducts({
        page: 1,
        per_page: 24,
        sort_by: 'published_at',
        sort_dir: 'desc',
      }).catch(() => null),
    ])

    // We don't know which categories will have products until all pages load.
    // For the initial render, include every category that has any product in the first page
    // OR that has children/description suggesting it is active — but to keep ordering stable,
    // we include ALL categories from listStorefrontCategories and let the client fill in products.
    const orderedCategories = [...categories].sort((a, b) => a.sort_order - b.sort_order)

    return {
      orderedCategories,
      firstPageProducts: firstPage?.items ?? [],
      totalProducts: firstPage?.total ?? 0,
    }
  },
  head: () => ({
    meta: [
      { title: 'Katalog Grosir Online: Produk Konveksi | Dropify' },
      {
        name: 'description',
        content:
          'Jelajahi katalog grosir Dropify dengan menggeser layar. Buka produk yang sesuai, lalu kirim nama produk, jumlah, dan kota tujuan melalui WhatsApp.',
      },
      { property: 'og:title', content: 'Katalog Grosir Online: Produk Konveksi | Dropify' },
      { property: 'og:description', content: 'Katalog grosir online Dropify dalam tampilan swipe interaktif.' },
      { property: 'og:url', content: absoluteUrl('/katalog') },
      { property: 'og:image', content: DEFAULT_OG_IMAGE },
      { name: 'twitter:card', content: 'summary_large_image' },
      { name: 'twitter:title', content: 'Katalog Grosir Online: Produk Konveksi | Dropify' },
      { name: 'twitter:image', content: DEFAULT_OG_IMAGE },
      { name: 'theme-color', content: '#0f172a' },
    ],
    links: [{ rel: 'canonical', href: absoluteUrl('/katalog') }],
  }),
  component: KatalogPage,
})

function findDividerIndex(slides: CatalogSlide[], slug: string) {
  return slides.findIndex((s) => s.kind === 'divider' && s.slug === slug)
}

function findCategoryRenderCount(slides: CatalogSlide[], slug: string) {
  const dividerIdx = findDividerIndex(slides, slug)
  if (dividerIdx < 0) return -1

  const nextDividerIdx = slides.findIndex(
    (slide, idx) => idx > dividerIdx && slide.kind === 'divider',
  )

  return nextDividerIdx > 0 ? nextDividerIdx : slides.length
}

function getSlideCategorySlug(slide: CatalogSlide | undefined) {
  if (!slide) return null
  if (slide.kind === 'divider' || slide.kind === 'status') return slide.slug
  if (slide.kind === 'product') return slide.categorySlug
  return null
}

function mergeProductsById(current: StorefrontProduct[], incoming: StorefrontProduct[]) {
  if (incoming.length === 0) return current
  const seen = new Set(current.map((product) => product.id))
  const next = [...current]
  for (const product of incoming) {
    if (seen.has(product.id)) continue
    seen.add(product.id)
    next.push(product)
  }
  return next
}

function normalizeRenderedCount(slides: CatalogSlide[], desiredCount: number) {
  const boundedCount = Math.max(0, Math.min(desiredCount, slides.length))
  if (boundedCount === 0 || boundedCount >= slides.length) return boundedCount

  // Never end the rendered window on a category divider. Include the next slide so
  // each visible category has either products or a status screen beneath the title.
  if (slides[boundedCount - 1]?.kind === 'divider') {
    return Math.min(boundedCount + 1, slides.length)
  }

  return boundedCount
}

function KatalogPage() {
  const { orderedCategories, firstPageProducts, totalProducts } =
    Route.useLoaderData()

  // Track the active slide by stable id so we can restore scroll position
  // after the slide list re-grows from background fetches.
  const containerRef = useRef<HTMLDivElement>(null)
  const sentinelRef = useRef<HTMLDivElement>(null)
  const pendingScrollIdRef = useRef<string | null>(null)
  const lastVisibleSlideIdRef = useRef<string | null>(null)
  const isRestoringRef = useRef(false)
  const requestedCategorySlugsRef = useRef(new Set<string>())

  const [allProducts, setAllProducts] = useState<StorefrontProduct[]>(firstPageProducts)
  const [activeIdx, setActiveIdx] = useState(0)
  const [renderedCount, setRenderedCount] = useState(INITIAL_RENDER)
  const [categoryFetchStates, setCategoryFetchStates] = useState<Record<string, CategoryFetchState>>({})

  const productsByCategory = useMemo(() => groupProductsByCategory(allProducts), [allProducts])
  const slides = useMemo(
    () => buildSlides(orderedCategories, productsByCategory, categoryFetchStates),
    [orderedCategories, productsByCategory, categoryFetchStates],
  )

  useEffect(() => {
    setRenderedCount((count) => normalizeRenderedCount(slides, count || INITIAL_RENDER))
  }, [slides])

  const ensureCategoryProducts = useCallback((slug: string | null | undefined) => {
    if (!slug) return
    const currentItems = productsByCategory.get(slug) ?? []
    if (currentItems.length > 0 && requestedCategorySlugsRef.current.has(slug)) return
    if (requestedCategorySlugsRef.current.has(slug)) return

    requestedCategorySlugsRef.current.add(slug)
    setCategoryFetchStates((prev) => ({ ...prev, [slug]: 'loading' }))

    let cancelled = false
    listStorefrontProductsServer({ data: {
      category: slug,
      page: 1,
      per_page: CATALOG_PER_PAGE,
      sort_by: 'published_at',
      sort_dir: 'desc',
    } })
      .then(async (firstPage) => {
        if (cancelled) return
        setAllProducts((prev) => mergeProductsById(prev, firstPage.items))

        if (firstPage.total_pages <= 1) {
          setCategoryFetchStates((prev) => ({
            ...prev,
            [slug]: firstPage.items.length > 0 ? 'ready' : 'empty',
          }))
          return
        }

        const restPages = Array.from({ length: firstPage.total_pages - 1 }, (_, i) => i + 2)
        const responses = await Promise.all(
          restPages.map((page) =>
            listStorefrontProductsServer({ data: {
              category: slug,
              page,
              per_page: CATALOG_PER_PAGE,
              sort_by: 'published_at',
              sort_dir: 'desc',
            } }),
          ),
        )
        if (cancelled) return
        const remainingItems = responses.flatMap((response) => response.items)
        setAllProducts((prev) => mergeProductsById(prev, remainingItems))
        setCategoryFetchStates((prev) => ({
          ...prev,
          [slug]: firstPage.items.length + remainingItems.length > 0 ? 'ready' : 'empty',
        }))
      })
      .catch(() => {
        if (!cancelled) setCategoryFetchStates((prev) => ({ ...prev, [slug]: 'error' }))
      })

    return () => {
      cancelled = true
    }
  }, [productsByCategory])

  // Hash deep-link on mount
  useEffect(() => {
    if (typeof window === 'undefined') return
    const hash = window.location.hash.slice(1)
    if (!hash || !hash.startsWith('kategori-')) return
    pendingScrollIdRef.current = hash
    const slug = hash.slice('kategori-'.length)
    ensureCategoryProducts(slug)
    const targetRenderCount = findCategoryRenderCount(slides, slug)
    if (targetRenderCount > 0) {
      setRenderedCount((n) => normalizeRenderedCount(slides, Math.max(n, targetRenderCount)))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Restore scroll to pending id once it exists
  useEffect(() => {
    const pending = pendingScrollIdRef.current
    if (!pending) return
    const target = document.getElementById(pending)
    if (!target) return
    requestAnimationFrame(() => {
      target.scrollIntoView({ block: 'start' })
      pendingScrollIdRef.current = null
    })
  }, [renderedCount, slides])

  // Fire GA4 `view_item_list` the first time a category becomes the active slide
  // and has products loaded. The swipe catalog has no per-category mount event,
  // so we hook the active-slide change. One event per category per session.
  const firedListsRef = useRef<Set<string>>(new Set())
  useEffect(() => {
    const activeCategorySlug = getSlideCategorySlug(slides[activeIdx])
    ensureCategoryProducts(activeCategorySlug)
    if (!activeCategorySlug || firedListsRef.current.has(activeCategorySlug)) return
    const categoryProducts = productsByCategory.get(activeCategorySlug)
    if (!categoryProducts || categoryProducts.length === 0) return
    const category = orderedCategories.find((c) => c.slug === activeCategorySlug)
    if (!category) return
    firedListsRef.current.add(activeCategorySlug)
    trackViewItemList(
      category.name,
      categoryProducts.slice(0, 20).map((product, index) => ({
        item_id: product.id,
        item_name: product.name,
        item_category: category.name,
        price: getProductPrice(product),
        index,
      })),
    )
  }, [activeIdx, ensureCategoryProducts, slides, productsByCategory, orderedCategories])

  // When slides grow due to background fetch, restore the user's scroll anchor
  // so they aren't visually pushed off their current slide
  useEffect(() => {
    const anchor = lastVisibleSlideIdRef.current
    if (!anchor) return
    const container = containerRef.current
    if (!container) return
    const target = document.getElementById(anchor)
    if (!target) return
    // Skip the restore on the very first slides build (TOC at top is desired)
    if (container.scrollTop === 0) return
    isRestoringRef.current = true
    target.scrollIntoView({ block: 'start' })
    requestAnimationFrame(() => {
      isRestoringRef.current = false
    })
  }, [slides])

  // Sentinel-based expansion when user approaches the rendered end
  useEffect(() => {
    if (renderedCount >= slides.length) return
    const root = containerRef.current
    const sentinel = sentinelRef.current
    if (!root || !sentinel) return
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          setRenderedCount((n) => normalizeRenderedCount(slides, n + EXPAND_BATCH))
        }
      },
      { root, rootMargin: '200% 0px 200% 0px', threshold: 0 },
    )
    observer.observe(sentinel)
    return () => observer.disconnect()
  }, [renderedCount, slides.length])

  // Active slide tracker + visible-slide-id anchor
  useEffect(() => {
    const container = containerRef.current
    if (!container) return
    const slideEls = container.querySelectorAll<HTMLElement>('[data-slide-idx]')
    if (slideEls.length === 0) return
    const observer = new IntersectionObserver(
      (entries) => {
        if (isRestoringRef.current) return
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => b.intersectionRatio - a.intersectionRatio)
        if (visible.length === 0) return
        const topEl = visible[0].target as HTMLElement
        const idx = Number(topEl.getAttribute('data-slide-idx'))
        if (!Number.isNaN(idx)) setActiveIdx(idx)
        if (topEl.id) lastVisibleSlideIdRef.current = topEl.id
      },
      { root: container, threshold: [0.5, 0.75] },
    )
    slideEls.forEach((el) => observer.observe(el))
    return () => observer.disconnect()
  }, [renderedCount, slides.length])

  // Keyboard navigation
  useEffect(() => {
    if (typeof window === 'undefined') return
    const handler = (event: KeyboardEvent) => {
      const container = containerRef.current
      if (!container) return
      const tag = (event.target as HTMLElement | null)?.tagName
      if (tag === 'INPUT' || tag === 'TEXTAREA') return
      if (event.key === 'ArrowDown' || event.key === 'PageDown' || event.key === ' ') {
        event.preventDefault()
        container.scrollBy({ top: container.clientHeight, behavior: 'smooth' })
      } else if (event.key === 'ArrowUp' || event.key === 'PageUp') {
        event.preventDefault()
        container.scrollBy({ top: -container.clientHeight, behavior: 'smooth' })
      } else if (event.key === 'Escape') {
        window.location.href = '/'
      }
    }
    window.addEventListener('keydown', handler)
    return () => window.removeEventListener('keydown', handler)
  }, [])

  const scrollToToc = useCallback(() => {
    const container = containerRef.current
    if (!container) return
    container.scrollTo({ top: 0, behavior: 'smooth' })
    if (typeof window !== 'undefined' && window.history.replaceState) {
      window.history.replaceState(null, '', '#daftar-isi')
    }
  }, [])

  const handleTocItemClick = useCallback(
    (slug: string) => {
      const id = dividerId(slug)
      const targetIdx = findDividerIndex(slides, slug)
      const targetRenderCount = findCategoryRenderCount(slides, slug)
      if (targetIdx < 0 || targetRenderCount < 0) return
      ensureCategoryProducts(slug)
      if (targetRenderCount > renderedCount) {
        pendingScrollIdRef.current = id
        setRenderedCount(normalizeRenderedCount(slides, targetRenderCount))
      } else {
        const target = document.getElementById(id)
        target?.scrollIntoView({ behavior: 'smooth', block: 'start' })
      }
      if (typeof window !== 'undefined' && window.history.replaceState) {
        window.history.replaceState(null, '', `#${id}`)
      }
    },
    [ensureCategoryProducts, renderedCount, slides],
  )

  const visibleSlides = useMemo(() => slides.slice(0, renderedCount), [slides, renderedCount])

  if (slides.length === 0) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-slate-900 text-white px-6 text-center">
        <h1 className="text-2xl font-black mb-3">Belum ada produk di katalog</h1>
        <p className="text-slate-400 text-sm mb-6">Silakan kembali sebentar lagi atau lihat halaman utama.</p>
        <a
          href="/"
          className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-bold px-5 py-3 rounded-xl transition-colors"
        >
          Kembali ke Beranda
        </a>
      </div>
    )
  }

  return (
    <div className="catalog-shell fixed inset-x-0 top-0 bg-black overflow-hidden">
      <h1 className="sr-only">Katalog Grosir Online Dropify: Distributor Tangan Pertama Indonesia</h1>
      <CatalogTopBar
        activeIdx={activeIdx}
        totalSlides={slides.length}
        totalProducts={totalProducts}
        totalCategories={orderedCategories.length}
        showBackToToc={activeIdx > 0}
        onBackToToc={scrollToToc}
        loading={Object.values(categoryFetchStates).some((state) => state === 'loading')}
      />

      <style>{`[data-catalog-scroll]::-webkit-scrollbar { display: none; }`}</style>
      <div
        ref={containerRef}
        data-catalog-scroll
        className="h-full w-full overflow-y-scroll snap-y snap-mandatory"
        style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
      >
        {visibleSlides.map((slide, idx) => {
          const nearby = Math.abs(idx - activeIdx) <= IMAGE_RENDER_RADIUS || (activeIdx === 0 && idx < INITIAL_RENDER)
          if (slide.kind === 'toc') {
            return (
              <TocSlide
                key="toc"
                entries={slide.entries}
                idx={idx}
                onItemClick={handleTocItemClick}
              />
            )
          }
          if (slide.kind === 'divider') {
            return (
              <DividerSlide
                key={`d-${slide.slug}`}
                slide={slide}
                idx={idx}
                renderContent={nearby}
              />
            )
          }
          if (slide.kind === 'status') {
            return (
              <CategoryStatusSlideView
                key={`s-${slide.slug}`}
                slide={slide}
                idx={idx}
                onRetry={() => {
                  requestedCategorySlugsRef.current.delete(slide.slug)
                  ensureCategoryProducts(slide.slug)
                }}
              />
            )
          }
          if (slide.kind === 'channel') {
            return (
              <section
                key="whatsapp-channel"
                data-slide-idx={idx}
                data-slide-kind="channel"
                className="catalog-slide flex snap-start items-center bg-slate-50"
              >
                <div className="w-full">
                  <WhatsAppChannelSection showSocksPromo={false} />
                </div>
              </section>
            )
          }
          return (
            <ProductSlide
              key={`p-${slide.id}`}
              slide={slide}
              idx={idx}
              priority={idx === activeIdx}
              renderContent={nearby}
            />
          )
        })}
        {renderedCount < slides.length && (
          <div
            ref={sentinelRef}
            data-rendering-sentinel
            style={{ height: 1 }}
            aria-hidden="true"
          />
        )}
      </div>
    </div>
  )
}

function CatalogTopBar({
  activeIdx,
  totalSlides,
  totalProducts,
  totalCategories,
  showBackToToc,
  onBackToToc,
  loading,
}: {
  activeIdx: number
  totalSlides: number
  totalProducts: number
  totalCategories: number
  showBackToToc: boolean
  onBackToToc: () => void
  loading: boolean
}) {
  const progress = ((activeIdx + 1) / totalSlides) * 100
  return (
    <header className="absolute top-0 inset-x-0 z-20 bg-gradient-to-b from-black/70 via-black/40 to-transparent pt-3 pb-10 pointer-events-none">
      <div className="px-4 flex items-center gap-3 pointer-events-auto">
        <a href="/" className="inline-flex items-center gap-2 text-white hover:opacity-90 transition-opacity flex-shrink-0">
          <img src="/logo40.webp" alt="Dropify" width="32" height="32" className="w-8 h-8 object-contain" />
          <div className="hidden sm:flex flex-col leading-tight">
            <span className="text-sm font-bold">Dropify</span>
            <span className="text-[10px] uppercase tracking-widest text-white/70">Katalog</span>
          </div>
        </a>

        <div className="flex-1 flex items-center justify-center">
          {showBackToToc && (
            <button
              type="button"
              onClick={onBackToToc}
              className="inline-flex items-center gap-2 bg-white/15 hover:bg-white/25 backdrop-blur-md text-white text-xs font-semibold px-3 py-2 rounded-full transition-colors"
              aria-label="Kembali ke daftar isi"
            >
              <FontAwesomeIcon icon={faList} className="w-3 h-3" />
              <span>Daftar Isi</span>
            </button>
          )}
        </div>

        <div className="hidden md:flex items-center gap-3 text-white/70 text-xs flex-shrink-0">
          <span>{totalCategories} kategori</span>
          <span className="w-1 h-1 rounded-full bg-white/40" />
          <span>{totalProducts} produk</span>
          {loading && (
            <>
              <span className="w-1 h-1 rounded-full bg-white/40" />
              <span className="flex items-center gap-1.5 text-blue-300">
                <span className="inline-block w-1.5 h-1.5 rounded-full bg-blue-400 animate-pulse" />
                memuat
              </span>
            </>
          )}
        </div>
        <a
          href="/"
          className="flex-shrink-0 w-10 h-10 flex items-center justify-center rounded-full bg-white/15 backdrop-blur-md hover:bg-white/25 text-white transition-colors"
          aria-label="Tutup katalog"
        >
          <FontAwesomeIcon icon={faXmark} className="w-4 h-4" />
        </a>
      </div>
      <div className="mt-3 px-4 pointer-events-auto">
        <div className="flex items-center gap-3">
          <div className="flex-1 h-0.5 bg-white/15 rounded-full overflow-hidden">
            <div className="h-full bg-white transition-all duration-300 ease-out" style={{ width: `${progress}%` }} />
          </div>
          <span className="text-white/80 text-[11px] font-bold tabular-nums">
            {activeIdx + 1}/{totalSlides}
          </span>
        </div>
      </div>
    </header>
  )
}

function TocSlide({
  entries,
  idx,
  onItemClick,
}: {
  entries: TocCategoryEntry[]
  idx: number
  onItemClick: (slug: string) => void
}) {
  return (
    <section
      id={TOC_SLIDE_ID}
      data-slide-idx={idx}
      data-slide-kind="toc"
      className="relative catalog-slide w-full snap-start snap-always overflow-hidden bg-gradient-to-br from-blue-950 via-slate-900 to-slate-950"
    >
      <div
        className="absolute inset-0 opacity-[0.08] pointer-events-none"
        aria-hidden="true"
        style={{ backgroundImage: 'radial-gradient(circle, white 1px, transparent 1px)', backgroundSize: '24px 24px' }}
      />
      <div className="absolute top-20 -left-20 w-72 h-72 rounded-full bg-blue-600/15 blur-3xl pointer-events-none" aria-hidden="true" />
      <div className="absolute bottom-10 -right-20 w-80 h-80 rounded-full bg-emerald-500/10 blur-3xl pointer-events-none" aria-hidden="true" />

      <div className="relative h-full max-w-md mx-auto px-5 flex flex-col pt-24 pb-10">
        <div className="text-center mb-5 flex-shrink-0">
          <p className="text-blue-300 text-[11px] font-bold uppercase tracking-[0.35em] mb-2">Daftar Isi</p>
          <h2 className="text-white text-3xl font-black uppercase tracking-tight mb-1.5">Pilih Kategori</h2>
          <p className="text-slate-400 text-xs">Tap salah satu di bawah, atau geser ke atas untuk mulai jelajahi.</p>
          <p className="mt-2 text-slate-400 text-xs">
            Dropify adalah supplier tangan pertama di Surabaya. Harga grosir langsung, kirim ke seluruh
            Indonesia, semua pemesanan dilayani via WhatsApp tanpa perlu aplikasi.
          </p>
          <a
            href={`https://wa.me/${WA_NUMBER}?text=${encodeURIComponent('Halo Dropify, saya sedang lihat katalog grosir. Mohon info harga dan minimum order.')}`}
            {...whatsAppCtaProps('catalog', {
              cta_placement: 'catalog_toc',
              lp_category: 'Katalog grosir',
            })}
            target="_blank"
            rel="noopener noreferrer"
            className="mt-3 inline-flex items-center justify-center gap-2 rounded-xl bg-green-700 hover:bg-green-800 px-5 py-2.5 text-xs font-bold text-white transition-colors"
          >
            <FontAwesomeIcon icon={faWhatsapp} className="w-3.5 h-3.5" />
            Tanya Harga Grosir via WhatsApp
          </a>
        </div>

        <ul className="flex-1 overflow-y-auto space-y-2 -mx-1 px-1 catalog-toc-list">
          {entries.map((entry, i) => (
            <TocItem
              key={entry.id}
              entry={entry}
              index={i}
              onClick={() => onItemClick(entry.slug)}
            />
          ))}
        </ul>

        <div className="mt-4 flex flex-col items-center gap-1.5 text-white/55 pointer-events-none flex-shrink-0">
          <div className="animate-bounce">
            <FontAwesomeIcon icon={faChevronUp} className="w-3.5 h-3.5" />
          </div>
          <span className="text-[10px] uppercase tracking-[0.25em] font-semibold">Atau geser ke atas</span>
        </div>
      </div>
    </section>
  )
}

function TocItem({
  entry,
  index,
  onClick,
}: {
  entry: TocCategoryEntry
  index: number
  onClick: () => void
}) {
  return (
    <li>
      <a
        href={`#${dividerId(entry.slug)}`}
        onClick={(event) => {
          event.preventDefault()
          onClick()
        }}
        className="flex items-center gap-3 bg-white/[0.04] hover:bg-white/10 active:bg-white/15 border border-white/10 rounded-2xl p-2.5 transition-colors group"
      >
        <span className="text-blue-300 text-[10px] font-black w-5 text-right tabular-nums flex-shrink-0">
          {String(index + 1).padStart(2, '0')}
        </span>
        <img
          src={entry.thumbUrl}
          alt={`Kategori ${entry.name}`}
          width="48"
          height="48"
          loading="lazy"
          decoding="async"
          className="w-12 h-12 rounded-xl object-cover flex-shrink-0 ring-1 ring-white/10"
        />
        <div className="flex-1 min-w-0">
          <p className="text-white text-sm font-bold leading-tight truncate">{entry.name}</p>
          {entry.productCount === null ? (
            <p className="text-slate-500 text-[11px] mt-0.5 flex items-center gap-1.5">
              <span className="inline-block w-1 h-1 rounded-full bg-blue-400 animate-pulse" />
              Lihat produk
            </p>
          ) : (
            <p className="text-slate-400 text-[11px] mt-0.5">{entry.productCount} produk</p>
          )}
        </div>
        <FontAwesomeIcon
          icon={faArrowRight}
          className="w-3.5 h-3.5 text-white/40 group-hover:text-white/80 group-hover:translate-x-0.5 transition-all flex-shrink-0"
        />
      </a>
    </li>
  )
}

function DividerSlide({
  slide,
  idx,
  renderContent,
}: {
  slide: CategoryDividerSlide
  idx: number
  renderContent: boolean
}) {
  return (
    <section
      id={dividerId(slide.slug)}
      data-slide-idx={idx}
      data-slide-kind="divider"
      data-slide-placeholder={!renderContent || undefined}
      className="relative catalog-slide w-full snap-start snap-always flex items-center justify-center overflow-hidden"
    >
      {renderContent && <>
      {/* Decorative category backdrop — rendered via CSS background so it carries
          no alt requirement (the slide heading conveys the meaning). */}
      <div
        aria-hidden="true"
        className="absolute inset-0 w-full h-full bg-cover bg-center opacity-40"
        style={{ backgroundImage: `url("${slide.imageUrl}")` }}
      />
      <div className="absolute inset-0 bg-gradient-to-br from-blue-900/80 via-slate-900/85 to-slate-950" />
      <div
        className="absolute inset-0 opacity-[0.08] pointer-events-none"
        aria-hidden="true"
        style={{ backgroundImage: 'radial-gradient(circle, white 1px, transparent 1px)', backgroundSize: '24px 24px' }}
      />

      <div className="relative max-w-md mx-auto px-6 text-center">
        <p className="text-blue-300 text-[11px] font-bold uppercase tracking-[0.35em] mb-5">
          Kategori {slide.index + 1} dari {slide.total}
        </p>
        <h2 className="text-white text-5xl md:text-6xl font-black uppercase tracking-tight leading-[0.95] mb-6">
          {slide.name}
        </h2>
        {slide.description && (
          <p className="text-slate-300 text-sm md:text-base leading-relaxed max-w-sm mx-auto">
            {slide.description}
          </p>
        )}
      </div>

      <div className="absolute bottom-10 inset-x-0 flex flex-col items-center gap-2 text-white/60 pointer-events-none">
        <div className="animate-bounce">
          <FontAwesomeIcon icon={faChevronUp} className="w-4 h-4" />
        </div>
        <span className="text-[10px] uppercase tracking-[0.25em] font-semibold">Mulai jelajahi</span>
      </div>
      </>}
    </section>
  )
}

function CategoryStatusSlideView({
  slide,
  idx,
  onRetry,
}: {
  slide: CategoryLoadingSlide
  idx: number
  onRetry: () => void
}) {
  if (slide.state === 'error') {
    return (
      <section
        id={`${dividerId(slide.slug)}-error`}
        data-slide-idx={idx}
        data-slide-kind="error"
        className="flex catalog-slide snap-start snap-always items-center justify-center bg-slate-950 px-6 text-center text-white"
      >
        <div>
          <h2 className="text-xl font-bold">Produk {slide.name} belum dapat dimuat</h2>
          <p className="mt-2 text-sm text-slate-300">Periksa koneksi lalu coba lagi.</p>
          <button type="button" onClick={onRetry} className="mt-5 min-h-11 rounded-lg bg-blue-600 px-5 py-2 text-sm font-semibold text-white hover:bg-blue-700">
            Muat ulang produk
          </button>
        </div>
      </section>
    )
  }

  if (slide.state === 'empty') {
    return (
      <section
        id={`${dividerId(slide.slug)}-empty`}
        data-slide-idx={idx}
        data-slide-kind="empty"
        className="relative catalog-slide w-full snap-start snap-always overflow-hidden bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950"
      >
        <div
          className="absolute inset-0 opacity-[0.06] pointer-events-none"
          aria-hidden="true"
          style={{ backgroundImage: 'radial-gradient(circle, white 1px, transparent 1px)', backgroundSize: '24px 24px' }}
        />
        <div className="relative h-full max-w-md mx-auto px-6 flex flex-col items-center justify-center text-center">
          <div className="w-16 h-16 rounded-full bg-white/5 border border-white/10 flex items-center justify-center mb-5">
            <FontAwesomeIcon icon={faList} className="w-6 h-6 text-white/40" />
          </div>
          <p className="text-white/50 text-[11px] font-bold uppercase tracking-[0.3em] mb-2">
            {slide.name}
          </p>
          <h2 className="text-white text-2xl font-black mb-2">Belum ada produk</h2>
          <p className="text-slate-400 text-sm max-w-xs">
            Tidak ada produk yang dapat ditampilkan untuk kategori ini. Coba kategori lain atau hubungi admin untuk meminta katalog.
          </p>
        </div>
      </section>
    )
  }

  return <ProductSkeletonSlide slide={slide} idx={idx} />
}

function ProductSkeletonSlide({
  slide,
  idx,
}: {
  slide: CategoryLoadingSlide
  idx: number
}) {
  return (
    <section
      id={`${dividerId(slide.slug)}-loading`}
      data-slide-idx={idx}
      data-slide-kind="loading"
      className="relative catalog-slide w-full snap-start snap-always overflow-hidden bg-black"
    >
      {/* Background: shimmering blurred panel mimicking product backdrop */}
      <div
        className="absolute inset-0 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900"
        aria-hidden="true"
      />
      <div
        className="absolute inset-0 opacity-50 catalog-skeleton-shimmer"
        aria-hidden="true"
      />
      <div className="absolute inset-x-0 top-0 h-40 bg-gradient-to-b from-black/70 via-black/30 to-transparent pointer-events-none" />
      <div className="absolute inset-x-0 bottom-0 h-3/5 bg-gradient-to-t from-black via-black/85 to-transparent pointer-events-none" />

      {/* Category tag at top (real info — tells user what's loading) */}
      <div className="absolute top-20 left-4 right-4 flex items-center justify-between z-10">
        <span className="bg-white/15 backdrop-blur-md text-white text-[10px] font-bold uppercase tracking-widest px-3 py-1.5 rounded-full">
          {slide.name}
        </span>
        <span className="inline-flex items-center gap-1.5 bg-white/10 backdrop-blur-md text-white/80 text-[10px] font-semibold px-2.5 py-1.5 rounded-full">
          <span className="relative flex w-1.5 h-1.5">
            <span className="absolute inline-flex w-full h-full rounded-full bg-blue-400 opacity-75 animate-ping" />
            <span className="relative inline-flex w-1.5 h-1.5 rounded-full bg-blue-400" />
          </span>
          Memuat
        </span>
      </div>

      {/* Center: skeleton "product image" block */}
      <div className="absolute inset-0 flex items-center justify-center px-10">
        <div className="w-full max-w-xs aspect-[3/4] rounded-2xl bg-white/[0.04] border border-white/5 catalog-skeleton-pulse overflow-hidden relative">
          <div className="absolute inset-0 catalog-skeleton-sweep" aria-hidden="true" />
        </div>
      </div>

      {/* Bottom: skeleton blocks for title and buttons */}
      <div className="absolute inset-x-0 bottom-0 p-5 pb-8 z-10">
        <div className="max-w-md mx-auto space-y-3">
          <div className="space-y-2">
            <div className="h-6 w-3/4 rounded-md bg-white/10 catalog-skeleton-pulse" />
            <div className="h-6 w-1/2 rounded-md bg-white/10 catalog-skeleton-pulse" />
          </div>
          <div className="h-3 w-2/3 rounded-md bg-white/[0.06] catalog-skeleton-pulse" />
          <div className="pt-2 flex flex-col gap-2">
            <div className="h-12 w-full rounded-xl bg-green-700/40 catalog-skeleton-pulse" />
            <div className="h-9 w-full rounded-xl bg-white/[0.06] catalog-skeleton-pulse" />
          </div>
        </div>
      </div>

      <style>{`
        @keyframes catalog-skeleton-pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.45; }
        }
        .catalog-skeleton-pulse {
          animation: catalog-skeleton-pulse 1.6s ease-in-out infinite;
        }
        @keyframes catalog-skeleton-sweep {
          0% { transform: translateX(-100%); }
          100% { transform: translateX(100%); }
        }
        .catalog-skeleton-sweep {
          background: linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.06) 50%, transparent 100%);
          animation: catalog-skeleton-sweep 1.8s ease-in-out infinite;
        }
        @keyframes catalog-skeleton-shimmer {
          0% { background-position: 0% 50%; }
          100% { background-position: 200% 50%; }
        }
        .catalog-skeleton-shimmer {
          background-image: linear-gradient(110deg, transparent 40%, rgba(255,255,255,0.04) 50%, transparent 60%);
          background-size: 200% 100%;
          animation: catalog-skeleton-shimmer 2.4s linear infinite;
        }
      `}</style>
    </section>
  )
}

function ProductSlide({
  slide,
  idx,
  renderContent,
  priority,
}: {
  slide: SlimProductSlide
  idx: number
  renderContent: boolean
  priority: boolean
}) {
  return (
    <section
      id={productSlideId(slide.id)}
      data-slide-idx={idx}
      data-slide-kind="product"
      data-slide-placeholder={!renderContent || undefined}
      data-product-id={slide.id}
      className="relative catalog-slide w-full snap-start snap-always overflow-hidden bg-black"
    >
      {renderContent && <>
      {/* Decorative blurred backdrop — CSS background, no alt requirement. */}
      <div
        aria-hidden="true"
        className="absolute inset-0 w-full h-full bg-cover bg-center scale-110 blur-2xl opacity-60"
        style={{ backgroundImage: `url("${slide.blurImageUrl}")` }}
      />
      <div className="absolute inset-0 bg-black/30" aria-hidden="true" />

      <img
        src={slide.imageUrl}
        srcSet={slide.imageSrcSet || undefined}
        sizes="(max-width: 640px) 100vw, 640px"
        alt={slide.name}
        width="1080"
        height="1920"
        loading={priority ? 'eager' : 'lazy'}
        decoding="async"
        fetchPriority={priority ? 'high' : 'auto'}
        className="absolute inset-0 w-full h-full object-contain"
      />

      <div className="absolute inset-x-0 top-0 h-40 bg-gradient-to-b from-black/70 via-black/30 to-transparent pointer-events-none" />
      <div className="absolute inset-x-0 bottom-0 h-3/5 bg-gradient-to-t from-black via-black/85 to-transparent pointer-events-none" />

      <div className="absolute top-20 left-4 right-4 flex items-center justify-between z-10">
        <a
          href={`/kategori/${slide.categorySlug}`}
          className="bg-white/15 backdrop-blur-md hover:bg-white/25 text-white text-[10px] font-bold uppercase tracking-widest px-3 py-1.5 rounded-full transition-colors"
        >
          {slide.categoryName}
        </a>
      </div>

      <div className="absolute inset-x-0 bottom-0 p-5 pb-8 z-10">
        <div className="max-w-md mx-auto">
          <h2 className="text-white text-2xl md:text-3xl font-black leading-tight mb-2 line-clamp-2">
            {slide.name}
          </h2>
          {slide.description && (
            <p className="text-white/80 text-xs md:text-sm leading-relaxed mb-4 line-clamp-2">
              {slide.description}
            </p>
          )}
          <div className="flex flex-col gap-2">
            <a
              href={waProductLink(slide.name, slide.slug)}
              {...whatsAppCtaProps('catalog', {
                  cta_placement: 'catalog_swipe',
                  lp_category: slide.categoryName,
                  lead_item_name: slide.name,
                  items: [{ item_id: slide.slug, item_name: slide.name }],
                })}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center gap-2 w-full bg-green-700 hover:bg-green-800 text-white text-sm font-bold py-3.5 rounded-xl transition-colors shadow-lg"
            >
              <FontAwesomeIcon icon={faWhatsapp} className="w-4 h-4" />
              Chat WhatsApp
            </a>
            <a
              href={`/produk/${slide.slug}`}
              onClick={() =>
                trackSelectItem(
                  { item_id: slide.id, item_name: slide.name, item_category: slide.categoryName },
                  slide.categoryName,
                )
              }
              className="inline-flex items-center justify-center gap-2 w-full bg-white/15 backdrop-blur-md border border-white/30 hover:bg-white/25 text-white text-xs font-semibold py-2.5 rounded-xl transition-colors"
            >
              Lihat Detail Produk
              <FontAwesomeIcon icon={faArrowRight} className="w-3 h-3" />
            </a>
          </div>
        </div>
      </div>
      </>}
    </section>
  )
}
